'use client';

/**
 * Home Page - Magnate Regalia Landing Page
 * =========================================
 *
 * A premium landing page with:
 * 1. Hero section with locked video background (100vh)
 * 2. Project introduction with compelling copy
 * 3. Building showcase with high-quality renders
 * 4. Key features and USPs
 * 5. Location advantages
 * 6. Call-to-action section
 *
 * The video is locked to the hero section only,
 * subsequent sections have LiquidEther animated background.
 */

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import LazyLiquidEther from '@/components/backgrounds/LazyLiquidEther';
import StaticGradientBackground from '@/components/backgrounds/StaticGradientBackground';
import { useScrollAnimations } from '@/hooks/useScrollAnimations';
import { useDevicePerformance } from '@/hooks/useDevicePerformance';

/**
 * YouTubeAutoplay Component
 * -------------------------
 * Embeds a YouTube video that autoplays (muted) when scrolled into view
 * and pauses when scrolled out of view.
 *
 * HOW IT WORKS:
 * 1. Uses YouTube IFrame Player API to get programmatic control over the video
 * 2. Intersection Observer detects when the container enters/leaves viewport
 * 3. When 30% visible → play, when less than 30% visible → pause
 *
 * WHY YOUTUBE IFRAME API:
 * - Regular iframes don't expose play/pause controls
 * - The API loads a special script that enables player.playVideo() / player.pauseVideo()
 * - We must wait for both the API to load AND the player to be ready
 */

// Extend Window interface to include YouTube API types
declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        config: {
          videoId: string;
          host?: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  destroy: () => void;
  getPlayerState: () => number;
}

function YouTubeAutoplay({ videoId, className }: { videoId: string; className?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const [isAPIReady, setIsAPIReady] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const playerContainerId = `youtube-player-${videoId}`;

  // Step 0: Wait until the player is close to the viewport before loading
  // anything from YouTube. The IFrame API + player is ~1 MB of third-party
  // JS and sets third-party cookies, so loading it on page load hurt both
  // Lighthouse Performance and Best Practices.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Step 1: Load YouTube IFrame API script (once globally)
  useEffect(() => {
    if (!isNearViewport) return;

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      setIsAPIReady(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (existingScript) {
      // Wait for it to load
      const checkAPI = setInterval(() => {
        if (window.YT && window.YT.Player) {
          setIsAPIReady(true);
          clearInterval(checkAPI);
        }
      }, 100);
      return () => clearInterval(checkAPI);
    }

    // Load the API script
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;

    // YouTube API calls this global function when ready
    window.onYouTubeIframeAPIReady = () => {
      setIsAPIReady(true);
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup: don't remove script as other instances might use it
    };
  }, [isNearViewport]);

  // Step 2: Create the YouTube player once API is ready
  useEffect(() => {
    if (!isAPIReady || playerRef.current) return;

    // Small delay to ensure DOM element exists
    const timer = setTimeout(() => {
      playerRef.current = new window.YT.Player(playerContainerId, {
        videoId: videoId,
        host: 'https://www.youtube-nocookie.com', // Privacy-enhanced mode, no third-party cookies until play
        playerVars: {
          autoplay: 0,        // Don't autoplay immediately - we control this
          mute: 1,            // Must be muted for autoplay to work
          loop: 1,            // Loop the video
          playlist: videoId,  // Required for loop to work
          controls: 1,        // Show controls
          rel: 0,             // Don't show related videos
          modestbranding: 1,  // Minimal YouTube branding
          playsinline: 1,     // Play inline on mobile (not fullscreen)
        },
        events: {
          onReady: () => {
            setIsPlayerReady(true);
          },
        },
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      // Destroy player on unmount
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [isAPIReady, videoId, playerContainerId]);

  // Step 3: Set up Intersection Observer to track visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Update visibility state based on intersection
          // Using 30% threshold - play when 30%+ visible
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.3,  // Trigger at 30% visibility
        rootMargin: '0px' // No margin adjustment
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Step 4: Play/Pause based on visibility
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current) return;

    if (isVisible) {
      // Play when visible
      playerRef.current.playVideo();
    } else {
      // Pause when not visible
      playerRef.current.pauseVideo();
    }
  }, [isVisible, isPlayerReady]);

  return (
    <div ref={containerRef} className={className}>
      {/* 16:9 aspect ratio wrapper */}
      <div className="relative w-full h-full">
        {/* YouTube player container - the API will inject the iframe here */}
        <div
          id={playerContainerId}
          className="absolute inset-0 w-full h-full"
        />

        {/* Loading placeholder - shown until player is ready */}
        {!isPlayerReady && (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
            {/* Shimmer effect */}
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            {/* Play button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/20">
                  <svg className="w-10 h-10 text-white/80 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <span className="text-white/70 text-sm">Loading video...</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * FullscreenVideoModal Component
 * -------------------------------
 * Fullscreen YouTube video overlay that covers the entire viewport.
 * Shows when user clicks "Watch Complete Walkthrough".
 * Uses React Portal to render at document.body level for proper z-index stacking.
 */
function FullscreenVideoModal({
  videoId,
  isOpen,
  onClose
}: {
  videoId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  // Use portal to render at document body level, above everything
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center">
      {/* Video Frame Container */}
      <div className="relative w-[95vw] h-[95vh] border border-white/20 rounded-2xl overflow-hidden bg-black">
        {/* Close button - top right, inside frame */}
        <button
          onClick={onClose}
          className="absolute top-4 right-[90px] z-10 flex items-center gap-2 px-4 py-2 bg-black/60 hover:bg-red-500/80 rounded-lg transition-colors group"
          aria-label="Close video"
        >
          <span className="text-white/80 group-hover:text-white text-sm font-medium">Close</span>
          <svg className="w-5 h-5 text-white/80 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* YouTube iframe */}
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title="Magnate Regalia - Complete Walkthrough"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full border-0"
        />
      </div>
    </div>,
    document.body
  );
}

/**
 * HeroVideo Component
 * -------------------
 * Background video for the hero. Kept as its own component so that attaching
 * the video (a state change after page load) re-renders only this element,
 * not the whole 1000-line home page.
 */
function HeroVideo({ paused }: { paused: boolean }) {
  const [heroVideoSrc, setHeroVideoSrc] = useState<string | null>(null);
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  // Defer the hero video until the page has finished loading.
  // The poster image (rendered with next/image + preload) is the LCP element;
  // attaching the ~36 MB video immediately made it compete with the poster,
  // fonts and JS for bandwidth (mobile LCP was 40+ s). Users who asked for
  // reduced motion or reduced data keep the still image.
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // @ts-expect-error - navigator.connection is not in all browsers
    const saveData = navigator.connection?.saveData === true;
    if (prefersReducedMotion || saveData) return;

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let attached = false;
    const attach = () => {
      if (attached) return;
      attached = true;
      const start = () => setHeroVideoSrc('/assets/videos/MAGNET%20regalia.mp4');
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(start, { timeout: 2000 });
      } else {
        timeoutId = setTimeout(start, 200);
      }
    };

    // Fallback: `load` waits for every in-flight image (e.g. lazy images the
    // user scrolled to), so don't let a slow image hold the video back forever.
    const fallbackId = setTimeout(attach, 4000);

    if (document.readyState === 'complete') {
      attach();
    } else {
      window.addEventListener('load', attach, { once: true });
    }

    return () => {
      clearTimeout(fallbackId);
      window.removeEventListener('load', attach);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, []);

  // Pause/resume hero video when modal opens/closes
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video || !heroVideoSrc) return;
    if (paused) {
      video.pause();
    } else {
      video.play().catch(() => {
        // Autoplay can be blocked (e.g. low-power mode) - poster stays visible
      });
    }
  }, [paused, heroVideoSrc]);

  if (!heroVideoSrc) return null;

  return (
    <video
      ref={heroVideoRef}
      src={heroVideoSrc}
      autoPlay
      muted
      loop
      playsInline
      aria-hidden="true"
      className="hero-video-element"
    />
  );
}

export default function Home() {
  const [isWalkthroughOpen, setIsWalkthroughOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Get device performance settings
  const performance = useDevicePerformance();

  // Initialize scroll animations (GSAP ScrollTrigger) - respects performance settings
  useScrollAnimations({
    enabled: performance.enableScrollAnimations,
    enableParallax: performance.enableParallax,
  });

  // Detect mobile for LiquidEther optimization
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  return (
    <div className="relative">
      {/* Fullscreen Walkthrough Video Modal */}
      <FullscreenVideoModal
        videoId="YrIlXflrM9E"
        isOpen={isWalkthroughOpen}
        onClose={() => setIsWalkthroughOpen(false)}
      />
      {/* Background - Performance-aware rendering */}
      {/* HIGH/MEDIUM tier: LiquidEther with WebGL fluid simulation */}
      {/* LOW tier: Static gradient fallback (no WebGL) */}
      <div className="fixed inset-0 z-0">
        {performance.enableLiquidEther ? (
          <LazyLiquidEther
            colors={['#D4A574', '#8B6914', '#C9A227']}
            mouseForce={20}
            cursorSize={100}
            resolution={performance.liquidEtherResolution}
            isViscous={false}
            viscous={30}
            autoDemo={true}
            autoSpeed={isMobile ? 0.3 : 0.5}
            autoIntensity={isMobile ? 1.5 : 2.2}
            autoResumeDelay={2000}
            style={{ width: '100%', height: '100%' }}
          />
        ) : (
          <StaticGradientBackground
            colors={['#D4A574', '#8B6914', '#C9A227']}
          />
        )}
      </div>
      {/* ============================================================
          HERO SECTION - Video with content below
          ============================================================ */}
      <section className="hero-section relative z-10 pt-20 md:pt-0">
        {/* Video Container
            Mobile: 100vw width, 16:9 aspect ratio, margin-top for header
            Desktop: True fullscreen 100vw x 100vh */}
        <div className="hero-video-container">
          {/* Poster: optimised by next/image and the LCP element - the video fades in over it.
              Next 16 deprecated `priority`; it now only preloads and no longer sets
              fetchpriority, so both are set explicitly. */}
          <Image
            src="/assets/images/Cam_04.webp"
            alt="Magnate Regalia tower exterior"
            fill
            preload
            fetchPriority="high"
            sizes="100vw"
            className="object-cover"
          />
          <HeroVideo paused={isWalkthroughOpen} />
          {/* Subtle gradient at bottom for transition */}
          <div className="absolute inset-x-0 bottom-0 h-16 md:h-32 bg-gradient-to-t from-black to-transparent" />

          {/* Watch Complete Walkthrough button - Bottom right on mobile, right center on desktop */}
          <button
            onClick={() => setIsWalkthroughOpen(true)}
            className="absolute right-3 bottom-10 md:right-12 md:bottom-auto md:top-1/2 md:-translate-y-1/2 z-10 flex items-center gap-1 md:gap-2 group"
          >
            <span className="text-white text-xs md:text-xl font-medium group-hover:text-amber-400 transition-colors">
              Watch Walkthrough
            </span>
            <svg
              className="w-4 h-4 md:w-6 md:h-6 text-white group-hover:text-amber-400 group-hover:translate-x-1 transition-all"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Scroll indicator on video - Desktop only */}
          <div className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
            <div className="flex flex-col items-center gap-2">
              <span className="text-white/80 text-xs tracking-widest uppercase">Scroll</span>
              <svg
                className="w-6 h-6 text-white/80"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Hero Content - Below the video */}
        <div className="relative z-10 flex flex-col items-center px-4 py-12 md:py-24 bg-black">
          {/* Tagline */}
          <p className="text-amber-400 font-medium tracking-[0.3em] uppercase text-base md:text-lg mb-4">
            Luxury Redefined
          </p>

          {/* Main Title */}
          <h1 className="text-4xl sm:text-5xl md:text-8xl font-bold text-white text-center mb-6 font-display">
            Magnate Regalia
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-3xl text-white/90 text-center max-w-3xl mb-4 font-light">
            Premium 4BHK & 5BHK Luxury Apartments
          </p>

          {/* Location */}
          <p className="text-white/85 text-center max-w-xl mb-12 flex items-center gap-2">
            <span className="text-amber-400">&#9679;</span>
            B/H Science City, Ahmedabad
          </p>

          {/* CTA Button */}
          <Link
            href="/contact?message=I%20would%20like%20to%20schedule%20a%20site%20visit%20to%20Magnate%20Regalia.%20Please%20contact%20me%20to%20arrange%20a%20convenient%20time."
            className="px-8 py-4 bg-amber-400 text-slate-900 font-semibold rounded-full hover:bg-amber-300 hover:shadow-lg hover:shadow-amber-400/25 active:scale-95 transition-all duration-300 hover:scale-105 text-center focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-black"
          >
            Schedule Site Visit
          </Link>
        </div>
      </section>

      {/* ============================================================
          CONTENT SECTIONS - with LiquidEther background
          ============================================================ */}
      <div className="relative z-10">

        {/* ============================================================
            PROJECT INTRODUCTION SECTION
            ============================================================ */}
        <section className="parallax-section relative py-20 md:py-32 z-10">
          {/* Gradient overlay for transition from video */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4">
            
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-12 xl:gap-24 items-start">
              {/* Text Column - Sticky on Desktop */}
              <div className="xl:sticky xl:top-32 space-y-6 md:space-y-12">
                {/* Intro + Highlights Block - Glass card on mobile */}
                <div className="mobile-glass-section text-center xl:text-left">
                  <p className="text-amber-400 font-medium tracking-[0.2em] uppercase text-base md:text-lg mb-6">
                    Welcome to Excellence
                  </p>
                  <h2 className="text-3xl md:text-6xl font-bold text-white leading-tight mb-2 font-display text-center xl:text-left">
                    Defined by Excellence.
                  </h2>
                  <p className="text-3xl md:text-6xl font-bold text-white/40 leading-tight mb-8 font-display text-center xl:text-left">
                    Crafted for You.
                  </p>

                  <div className="space-y-4 text-sm md:text-base text-white/80 leading-relaxed font-light text-center xl:text-left mb-8">
                    <p>
                      Magnate Regalia isn&apos;t just a residence; it&apos;s a statement. Rising above the skyline of Science City, it stands as a beacon of uncompromising luxury, developed by the esteemed Magnate Group.
                    </p>
                    <p>
                      Nestled in the serene environs behind Science City, it offers the perfect duality: instant access to the S.P. Ring Road for connectivity, yet a world apart in its tranquility. Every 4BHK and 5BHK residence is a masterpiece of spatial design.
                    </p>
                  </div>

                  {/* Key Highlights */}
                  <div className="space-y-4 text-left">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-amber-400/10 flex items-center justify-center shrink-0 border border-amber-400/20">
                        <span className="text-amber-400 text-lg md:text-xl">✦</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-base md:text-lg">Uncompromising Quality</h3>
                        <p className="text-white/80 text-sm md:text-base">Premium imported marble flooring and designer fittings throughout.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-amber-400/10 flex items-center justify-center shrink-0 border border-amber-400/20">
                        <span className="text-amber-400 text-lg md:text-xl">⚡</span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-base md:text-lg">Future Ready</h3>
                        <p className="text-white/80 text-sm md:text-base">Fully integrated smart home automation for modern living.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats Row - Separate Glass card on mobile */}
                <div className="mobile-glass-section stagger-container grid grid-cols-3 gap-4 md:gap-8 md:pt-8 md:border-t md:border-white/10">
                  <div className="stagger-item text-center md:text-left">
                    <p className="stat-number text-3xl md:text-5xl font-bold text-white mb-2" data-value="15">15<span className="text-amber-400">+</span></p>
                    <p className="text-white/85 text-xs md:text-sm tracking-wider uppercase">Floors</p>
                  </div>
                  <div className="stagger-item text-center md:text-left">
                    <p className="text-3xl md:text-5xl font-bold text-white mb-2">4-5</p>
                    <p className="text-white/85 text-xs md:text-sm tracking-wider uppercase">BHK Options</p>
                  </div>
                  <div className="stagger-item text-center md:text-left">
                    <p className="stat-number text-3xl md:text-5xl font-bold text-white mb-2" data-value="50">50<span className="text-amber-400">+</span></p>
                    <p className="text-white/85 text-xs md:text-sm tracking-wider uppercase">Amenities</p>
                  </div>
                </div>
              </div>

              {/* Image Column - Masonry Layout - Starts below header on desktop */}
              <div className="stagger-container grid grid-cols-2 gap-4 xl:mt-32">
                <div className="space-y-4 pt-12">
                  <div className="stagger-item relative h-[300px] w-full rounded-2xl overflow-hidden">
                     <Image
                      src="/assets/images/Cam_02.webp"
                      alt="Magnate Regalia Exterior"
                      fill
                      loading="lazy"
                      sizes="(max-width: 640px) 100vw, 50vw"
                      className="object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="stagger-item relative h-[200px] w-full rounded-2xl overflow-hidden bg-slate-800">
                    <div className="absolute inset-0 flex items-center justify-center text-white/20">
                      <span className="text-4xl">⚜️</span>
                    </div>
                     <Image
                      src="/assets/images/Cam_15.webp"
                      alt="Interior Detail"
                      fill
                      loading="lazy"
                      sizes="(max-width: 640px) 100vw, 50vw"
                      className="object-cover hover:scale-105 transition-transform duration-300 opacity-80 hover:opacity-100"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="stagger-item relative h-[200px] w-full rounded-2xl overflow-hidden bg-slate-800">
                     <div className="absolute inset-0 flex items-center justify-center text-white/20">
                      <span className="text-4xl">✨</span>
                    </div>
                     <Image
                      src="/assets/images/Cam_24.webp"
                      alt="Entrance Detail"
                      fill
                      loading="lazy"
                      sizes="(max-width: 640px) 100vw, 50vw"
                      className="object-cover hover:scale-105 transition-transform duration-300 opacity-80 hover:opacity-100"
                    />
                  </div>
                  <div className="stagger-item relative h-[400px] w-full rounded-2xl overflow-hidden">
                    <Image
                      src="/assets/images/Cam_04.webp"
                      alt="Tower View"
                      fill
                      loading="lazy"
                      sizes="(max-width: 640px) 100vw, 50vw"
                      className="object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

      {/* ============================================================
          LOCATION SECTION
          ============================================================ */}
      <section className="parallax-section relative py-20 md:py-32 z-10">
        <div className="max-w-6xl mx-auto px-4">
          {/* Section Header + Video + Locations - Single glass card on mobile */}
          <div className="mobile-glass-section">
            <div className="text-center mb-8">
              <p className="text-amber-400 font-medium tracking-[0.2em] uppercase text-base md:text-lg mb-4">
                Prime Location
              </p>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-display text-center">
                Connected to Everything
              </h2>
              <p className="text-white/85 leading-relaxed max-w-2xl mx-auto text-sm md:text-base">
                Nestled in the heart of Ahmedabad&apos;s most sought-after neighborhood, Magnate Regalia offers unmatched connectivity to the city&apos;s finest destinations while maintaining the serenity of a premium residence.
              </p>
            </div>

            {/* Location Video */}
            <div className="mb-8">
              <YouTubeAutoplay
                videoId="GJxfalJpTZc"
                className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl"
              />
            </div>

            {/* Nearby Locations - Grid inside card */}
            <div className="stagger-container grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 pb-4 md:pb-0">
              <div className="stagger-item flex items-center gap-2 md:gap-4 p-2 md:p-4 glass-card rounded-xl">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-amber-400/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-amber-400 text-sm md:text-xl">&#127979;</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base">Science City</h3>
                  <p className="text-white/80 text-xs md:text-sm">2 min drive</p>
                </div>
              </div>

              <div className="stagger-item flex items-center gap-2 md:gap-4 p-2 md:p-4 glass-card rounded-xl">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-amber-400/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-amber-400 text-sm md:text-xl">&#128739;</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base">S.P. Ring Road</h3>
                  <p className="text-white/80 text-xs md:text-sm">Direct access</p>
                </div>
              </div>

              <div className="stagger-item flex items-center gap-2 md:gap-4 p-2 md:p-4 glass-card rounded-xl">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-amber-400/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-amber-400 text-sm md:text-xl">&#9992;</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base">Airport</h3>
                  <p className="text-white/80 text-xs md:text-sm">25 min drive</p>
                </div>
              </div>

              <div className="stagger-item flex items-center gap-2 md:gap-4 p-2 md:p-4 glass-card rounded-xl">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-amber-400/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-amber-400 text-sm md:text-xl">&#127963;</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base">Schools</h3>
                  <p className="text-white/80 text-xs md:text-sm">Within 5 km</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          BUILDING SHOWCASE SECTION
          ============================================================ */}
      <section className="parallax-section relative py-20 md:py-32 z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Header + Images - Single glass card on mobile */}
          <div className="mobile-glass-section">
            <div className="text-center mb-8 md:mb-16">
              <p className="text-amber-400 font-medium tracking-[0.2em] uppercase text-base md:text-lg mb-4">
                Architectural Marvel
              </p>
              <h2 className="text-3xl md:text-6xl font-bold text-white mb-6 font-display text-center">
                Where Vision Meets Reality
              </h2>
              <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto">
                Every angle of Magnate Regalia has been designed to inspire. Experience living in a masterpiece of modern architecture.
              </p>
            </div>

            {/* Parallax Strip Layout - Dynamic Visuals */}
            <div className="space-y-4 md:space-y-8">
            
            {/* Top Strip - Sky/High View */}
            <div className="relative h-[250px] md:h-[350px] w-full rounded-3xl overflow-hidden group">
              <Image
                src="/assets/images/Cam_04.webp"
                alt="Magnate Regalia - Architectural Excellence"
                fill
                className="object-cover"
                sizes="(max-width: 1280px) 100vw, 1280px"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 md:bottom-12 md:left-12 max-w-lg">
                <h3 className="text-xl md:text-4xl font-bold text-white mb-2">Rising Above</h3>
                <p className="text-white/80 text-sm md:text-base">A skyline-defining silhouette that commands attention from every angle.</p>
              </div>
            </div>

            {/* Split Strip - Details */}
            <div className="stagger-container grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 md:h-[300px]">
              <div className="stagger-item relative h-[200px] md:h-auto rounded-3xl overflow-hidden group">
                <Image
                  src="/assets/images/Cam_05.webp"
                  alt="Building Detail"
                  fill
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, 33vw"
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors" />
              </div>
              <div className="stagger-item relative h-[200px] md:h-auto rounded-3xl overflow-hidden group md:col-span-2">
                <Image
                  src="/assets/images/Cam_06.webp"
                  alt="Facade View"
                  fill
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, 66vw"
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 md:bottom-8 md:left-8">
                  <h3 className="text-white font-bold text-lg md:text-xl">Modern Facade</h3>
                  <p className="text-white/80 text-xs md:text-sm">Contemporary aesthetics that stand the test of time.</p>
                </div>
              </div>
            </div>

            {/* Bottom Strip - Ground/Entrance */}
            <div className="stagger-container grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 md:h-[300px]">
               <div className="stagger-item relative h-[200px] md:h-auto rounded-3xl overflow-hidden group md:col-span-2 order-2 md:order-1">
                <Image
                  src="/assets/images/Cam_08.webp"
                  alt="Landscape View"
                  fill
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, 66vw"
                  className="object-cover"
                />
                 <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                 <div className="absolute bottom-4 left-4 md:bottom-8 md:left-8">
                  <h3 className="text-white font-bold text-lg md:text-xl">Serene Landscapes</h3>
                  <p className="text-white/80 text-xs md:text-sm">Lush greenery integrated into the living experience.</p>
                </div>
              </div>
              <div className="stagger-item parallax-image-fast relative h-[200px] md:h-auto rounded-3xl overflow-hidden group order-1 md:order-2">
                <Image
                  src="/assets/images/Cam_11.webp"
                  alt="Entrance Detail"
                  fill
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, 33vw"
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors" />
              </div>
            </div>

          </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          APARTMENTS SECTION
          ============================================================ */}
      <section className="parallax-section relative py-20 md:py-32 z-10">
        <div className="max-w-6xl mx-auto px-4">
          {/* Section Header + Apartment Cards - Single glass card on mobile */}
          <div className="mobile-glass-section">
            <div className="text-center mb-8 md:mb-16">
              <p className="text-amber-400 font-medium tracking-[0.2em] uppercase text-base md:text-lg mb-4">
                Residences
              </p>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-display text-center">
                Spacious Living Spaces
              </h2>
              <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto">
                Choose from our thoughtfully designed 4BHK and 5BHK configurations, each offering generous living areas and premium finishes.
              </p>
            </div>

            {/* Apartment Cards */}
            <div className="stagger-container grid md:grid-cols-2 gap-8 pb-4 md:pb-0">
            {/* 4BHK Card */}
            <div className="stagger-item glass-card rounded-3xl overflow-hidden group">
              <div className="relative h-64">
                <Image
                  src="/assets/images/Cam_15.webp"
                  alt="4BHK Apartment"
                  fill
                  loading="lazy"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="px-4 py-2 bg-amber-400 text-slate-900 font-semibold rounded-full text-sm">
                    4 BHK
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-3 font-display">
                  4BHK Luxury Apartments
                </h3>
                <p className="text-white/85 mb-4">
                  Perfectly designed for modern families, featuring spacious bedrooms,
                  elegant living areas, and a fully equipped modular kitchen.
                </p>
                <ul className="space-y-2 text-white/80 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400">&#10003;</span>
                    Premium Italian marble flooring
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400">&#10003;</span>
                    Smart home automation ready
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400">&#10003;</span>
                    Private balconies with city views
                  </li>
                </ul>
                <Link
                  href="/layouts"
                  className="inline-flex items-center gap-2 text-amber-400 font-medium hover:gap-3 transition-all focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded"
                >
                  View Floor Plans
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* 5BHK Card */}
            <div className="stagger-item glass-card rounded-3xl overflow-hidden group">
              <div className="relative h-64">
                <Image
                  src="/assets/images/Cam_19.webp"
                  alt="5BHK Apartment"
                  fill
                  loading="lazy"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="px-4 py-2 bg-amber-400 text-slate-900 font-semibold rounded-full text-sm">
                    5 BHK
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-3 font-display">
                  5BHK Ultra-Luxury Residences
                </h3>
                <p className="text-white/85 mb-4">
                  For those who demand the extraordinary. Expansive living spaces
                  with dedicated home office, entertainment room, and private terrace.
                </p>
                <ul className="space-y-2 text-white/80 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400">&#10003;</span>
                    Dedicated home office space
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400">&#10003;</span>
                    Private terrace garden
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400">&#10003;</span>
                    Premium designer fittings
                  </li>
                </ul>
                <Link
                  href="/layouts"
                  className="inline-flex items-center gap-2 text-amber-400 font-medium hover:gap-3 transition-all focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded"
                >
                  View Floor Plans
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          AMENITIES PREVIEW SECTION
          ============================================================ */}
      <section className="parallax-section relative py-20 md:py-32 z-10">
        <div className="max-w-6xl mx-auto px-4">
          {/* Section Header + Amenities Grid - Single glass card on mobile */}
          <div className="mobile-glass-section">
            <div className="text-center mb-8 md:mb-16">
              <p className="text-amber-400 font-medium tracking-[0.2em] uppercase text-base md:text-lg mb-4">
                World-Class Amenities
              </p>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-display text-center">
                Elevate Your Lifestyle
              </h2>
              <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto">
                From fitness to relaxation, every amenity has been thoughtfully curated to enhance your daily living experience.
              </p>
            </div>

            {/* Amenities Grid */}
            <div className="relative">
            {/* Horizontal Scroll Container */}
            <div className="stagger-container flex md:grid md:grid-cols-3 gap-6 overflow-x-auto md:overflow-visible pb-8 md:pb-0 snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
            <div className="stagger-item relative h-64 w-[85vw] sm:w-[350px] md:w-auto shrink-0 snap-center rounded-2xl overflow-hidden group">
              <Image
                src="/assets/images/GYM_JPEG.webp"
                alt="Fitness Center"
                fill
                loading="lazy"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 85vw, 33vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <h3 className="text-white font-semibold text-lg">Fitness Center</h3>
              </div>
            </div>

            <div className="stagger-item relative h-64 w-[85vw] sm:w-[350px] md:w-auto shrink-0 snap-center rounded-2xl overflow-hidden group">
              <Image
                src="/assets/images/Theater_JPEG.webp"
                alt="Private Theater"
                fill
                loading="lazy"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 85vw, 33vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <h3 className="text-white font-semibold text-lg">Private Theater</h3>
              </div>
            </div>

            <div className="stagger-item relative h-64 w-[85vw] sm:w-[350px] md:w-auto shrink-0 snap-center rounded-2xl overflow-hidden group">
              <Image
                src="/assets/images/Banquet_Final_JPEG.webp"
                alt="Banquet Hall"
                fill
                loading="lazy"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 85vw, 33vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <h3 className="text-white font-semibold text-lg">Banquet Hall</h3>
              </div>
            </div>

            <div className="stagger-item relative h-64 w-[85vw] sm:w-[350px] md:w-auto shrink-0 snap-center rounded-2xl overflow-hidden group">
              <Image
                src="/assets/images/CAFE_JPEG.webp"
                alt="Cafe Lounge"
                fill
                loading="lazy"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 85vw, 33vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <h3 className="text-white font-semibold text-lg">Cafe Lounge</h3>
              </div>
            </div>

            <div className="stagger-item relative h-64 w-[85vw] sm:w-[350px] md:w-auto shrink-0 snap-center rounded-2xl overflow-hidden group">
              <Image
                src="/assets/images/Sauna_JPEG.webp"
                alt="Spa & Sauna"
                fill
                loading="lazy"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 85vw, 33vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <h3 className="text-white font-semibold text-lg">Spa & Sauna</h3>
              </div>
            </div>

            <div className="stagger-item relative h-64 w-[85vw] sm:w-[350px] md:w-auto shrink-0 snap-center rounded-2xl overflow-hidden group">
              <Image
                src="/assets/images/INDOOR GAMES_JPEG.webp"
                alt="Indoor Games"
                fill
                loading="lazy"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 85vw, 33vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <h3 className="text-white font-semibold text-lg">Indoor Games</h3>
              </div>
            </div>
            </div>
             {/* Swipe Indicator (Mobile Only) */}
            <div className="md:hidden flex justify-center mt-4">
              <div className="flex items-center gap-2 text-white/80 text-sm animate-bounce-x bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                <span>Swipe to explore</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link
              href="/amenities"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border border-white/30 hover:bg-white/20 hover:border-white/50 hover:shadow-lg hover:shadow-white/10 active:scale-95 transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-black"
            >
              Explore All Amenities
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          CTA SECTION
          ============================================================ */}
      <section className="parallax-section relative py-20 md:py-32 overflow-hidden z-10">
        {/* Background Image - Twin towers at dusk, aspirational for "Dream Home" */}
        <div className="absolute inset-0">
          <Image
            src="/assets/images/Cam_02.webp"
            alt="Magnate Regalia Twin Towers"
            fill
            loading="lazy"
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>

        {/* Content - No card wrapper needed */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-amber-400 font-medium tracking-[0.2em] uppercase text-base md:text-lg mb-4">
            Begin Your Journey
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-6 font-display text-center">
            Your Dream Home Awaits
          </h2>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-12">
            Experience the pinnacle of luxury living at Magnate Regalia. Schedule a site visit today and discover your future home.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            href="/contact?message=I%20would%20like%20to%20schedule%20a%20site%20visit%20to%20Magnate%20Regalia.%20Please%20contact%20me%20to%20arrange%20a%20convenient%20time."
            className="px-8 py-4 md:px-10 md:py-5 bg-amber-400 text-slate-900 font-bold rounded-full hover:bg-amber-300 hover:shadow-lg hover:shadow-amber-400/25 active:scale-95 transition-all duration-300 hover:scale-105 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-black"
          >
            Schedule Site Visit
          </Link>
          <a
            href="tel:+919664805202"
            className="px-8 py-4 md:px-10 md:py-5 bg-white/10 backdrop-blur-sm text-white font-bold rounded-full border border-white/30 hover:bg-white/20 hover:border-white/50 hover:shadow-lg hover:shadow-white/10 active:scale-95 transition-all duration-300 hover:scale-105 text-base md:text-lg flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-black"
          >
            <span>&#128222;</span>
            Call Now
          </a>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-8 mt-16 text-white/70">
            <div className="flex items-center gap-2">
              <span className="text-amber-400">&#10003;</span>
              <span>RERA Registered</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-amber-400">&#10003;</span>
              <span>Bank Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-amber-400">&#10003;</span>
              <span>Ready Possession</span>
            </div>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}
